//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import React, { ReactElement } from 'react'
import { Typography, Box, Paper } from '@mui/material'
import { FieldMetaData, StringTypeEnum } from '@shared/SchemaTypes'
import ViewItem from './ViewItem'
import { SchemaDescription, SchemaFieldDescription } from 'yup'
import {
  ERC20Wei,
  erc20WeiToCommaDecString,
  timestampToUTCDateString,
  timestampToUTCDateTimeString,
  timestampToUTCTimeString,
} from '@shared/conversions'

export function viewNumberString(
  value: string | undefined,
  fieldSchema: SchemaFieldDescription,
  dataValue: string | object | number,
  poolTokenSymbol: string,
  liquidityTokenSymbol: string
): string {
  let displayValue = value

  const schemaDescription = fieldSchema as SchemaDescription
  const fieldMetaData = schemaDescription.meta as FieldMetaData
  const stringType = fieldMetaData.stringType as string | undefined

  if (stringType === StringTypeEnum.OFFCHAIN_RATE) {
    displayValue = `${Number(dataValue).toFixed(0) ?? ''} BPS`
  }
  if (stringType === StringTypeEnum.UNIX_DATE) {
    displayValue = new Date(Number(dataValue)).toUTCString().replace('GMT', 'UTC')
  }

  if (stringType === StringTypeEnum.STABLE_COINS) {
    if (!dataValue) {
      displayValue = ''
    } else {
      displayValue = `${erc20WeiToCommaDecString(dataValue as ERC20Wei)} ${liquidityTokenSymbol} `
    }
  }
  if (stringType === StringTypeEnum.BPS) {
    displayValue = `${value ?? ''} BPS`
  }
  if (stringType === StringTypeEnum.TIME_INTERVAL_DAYS) {
    displayValue = `${value ?? ''} days`
  }
  if (stringType === StringTypeEnum.TIME_INTERVAL_SECONDS) {
    // prettier-ignore
    /* eslint-disable prettier/prettier */
    displayValue = `${
      !value
        ? '0 Seconds'
        : Number(value) >= 86400
        ? (Number(value) / 86400).toString() + ' days'
        : (Number(value) / 3600).toString() + ' hours'
    } `
  }
  if (stringType === StringTypeEnum.ETH_DATE) {
    displayValue = timestampToUTCDateString(dataValue)
  }
  if (stringType === StringTypeEnum.ETH_TIME) {
    displayValue = timestampToUTCTimeString(dataValue)
  }
  if (stringType === StringTypeEnum.ETH_DATE_TIME) {
    displayValue = timestampToUTCDateTimeString(dataValue)
  }
  if (stringType === StringTypeEnum.ETH_TIME_OF_DAY) {
    // seconds from 0:00 UTC e.g. 64800 is 18:00 UTC
    const secondsSinceMidnight = Number(dataValue)
    const hour = Math.floor(secondsSinceMidnight / 3600)
    const rem = secondsSinceMidnight % 3600
    const min = Math.floor(rem / 60)
    const minDisplay = min < 10 ? `0${min}` : `${min}`
    displayValue = `${hour}:${minDisplay}  UTC`
  }
  if (stringType === StringTypeEnum.SINGLE_DAY_APR) {
    displayValue = `${(Number(dataValue) / 1e8 / 1e8).toFixed(6) ?? ''} %`
  }
  if (stringType === StringTypeEnum.APR) {
    displayValue = `${(Number(dataValue) / 1e2).toFixed(2) ?? ''} %`
  }
  if (stringType === StringTypeEnum.PPT_SHARES) {
    displayValue = `${value ? erc20WeiToCommaDecString(dataValue as ERC20Wei) : ''} ${poolTokenSymbol}`
  }
  if (stringType === StringTypeEnum.EXCHANGE_RATE) {
    displayValue = `${value ? (Number(dataValue) / 1e18)?.toFixed(6) : ''} ${liquidityTokenSymbol}/${poolTokenSymbol}`
  }
  if (fieldMetaData.stateToString) {
    displayValue = `${(fieldMetaData.stateToString as Record<number, string>)[Number(value)]}`
  }
  return displayValue as string
}

export default function ViewNumber(props: {
  value: string | undefined
  label: string
  inTable: boolean
  fieldSchema: SchemaFieldDescription
  dataValue: string | object
  toolTip?: string
  poolTokenSymbol: string
  liquidityTokenSymbol: string
  card?: boolean
}): ReactElement {
  const { value, fieldSchema, dataValue, label, toolTip, poolTokenSymbol, liquidityTokenSymbol, card } = props

  const displayValue = viewNumberString(value, fieldSchema, dataValue, poolTokenSymbol, liquidityTokenSymbol)

  if (props.inTable) {
    return (
      <React.Fragment>
        <Typography variant="caption" gutterBottom component="div">
          {displayValue}
        </Typography>
      </React.Fragment>
    )
  }

  const content = (
    <ViewItem toolTip={toolTip}>
      <Typography variant="bodyMuted" gutterBottom component="div">
        {label}
      </Typography>
      <Typography variant="bodyBold" gutterBottom component="div">
        {displayValue}
      </Typography>
    </ViewItem>
  )

  if (card) {
    return (
      <Box
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 2,
          boxShadow: '0 1px 4px 0 rgba(16,30,54,0.04)',
          border: '1px solid #F2F3F6',
          px: 3,
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        {content}
      </Box>
    )
  }
  return content
}
